// The front yard's or the Podwórko's picture under the live sky: the cut picture of the current look and level
// (scenes/yardLooks.mjs), crossfading into the next look when the phase changes (render/phasedLook.js), with the
// shared sky behind it (farm/outdoorSky.js) down to that picture's horizon. The Podwórko's foreground (the altana
// bench, the barrel, the logs) is copied from the same look, so it fades with the ground under it. Shared by the
// owner's scenes and the visits; `level()` is read live (the host's on a visit): a new level is fetched and drawn
// as soon as its picture is in, the old one staying until then (an upgrade loads it first anyway).
import { createPhasedLook } from '../render/phasedLook.js';
import { attachOutdoorSky } from '../farm/outdoorSky.js';
import { applyForeground } from './sceneForeground.js';
import { BACKYARD_BG_PLACEMENT, backyardForegroundKey } from './backyardLayout.js';
import { ensureLoaded } from '../ui/sceneLoading.js';
import { YARD_LOOKS, yardLookFor, yardRects, yardHorizonY, yardSkyPanX, yardPrefetch, levelToDraw, levelKeys } from './yardLooks.mjs';

// How far above the horizon the clouds ride (farm/backdropFx.js createClouds): inside each yard's visible sky band.
const CLOUD_ROWS = {
  front: { far: { min: 210, max: 270 }, near: { min: 240, max: 330 } },
  back: { far: { min: 130, max: 180 }, near: { min: 150, max: 230 } },
};

// `prioritise`: re-plans the background loader after a fade (a visit passes the host's state, phasedLook.js).
export function attachYardLook(scene, { side, phase, level, prioritise }) {
  let lvl = level();
  const look = createPhasedLook(scene, {
    phase,
    looks: YARD_LOOKS,
    lookFor: yardLookFor,
    layersFor: (l) => yardRects(side, l, lvl),
    foregroundFor: (l, rects) => {
      if (side !== 'back') return null;
      const key = backyardForegroundKey(lvl);
      return key ? applyForeground(scene, key, rects.at(-1).key, BACKYARD_BG_PLACEMENT) : null;   // the near picture
    },
    prefetch: (l) => yardPrefetch(side, l, lvl, { lean: true }),
    prioritise,
    ownOutlines: true,   // each look was cut on its own (scripts/yard_sky.py)
    stacked: true,       // the far layer and the near picture: every far below every near, even mid-fade
  });
  const sky = attachOutdoorSky(scene, {
    horizonY: () => yardHorizonY(side, lvl),
    panX: () => yardSkyPanX(side, scene.cameras.main.scrollX),
    cloudRows: CLOUD_ROWS[side],
  });
  scene.events.once('shutdown', () => look.destroy());
  let asked = null;
  return {
    get shownLook() { return look.shownLook; },
    get shownKey() { return yardRects(side, look.shownLook, lvl).at(-1).key; },   // the near picture
    update(phaseNow, dtMs) {
      const wanted = level();
      const keysAt = (l) => levelKeys(side, [look.shownLook, yardLookFor(phaseNow)], l);
      if (wanted !== lvl && asked !== wanted) { asked = wanted; ensureLoaded(scene, keysAt(wanted)).catch(() => {}); }
      const next = levelToDraw(lvl, wanted, (l) => keysAt(l).every((k) => scene.textures.exists(k)));
      if (next !== lvl) { lvl = next; asked = null; look.redraw(); }
      look.update(phaseNow, dtMs);
      sky.update();
    },
  };
}
